import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Add as AddIcon,
  Link as LinkIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/firebase/auth.service';
import { organizationsService } from '../services/firebase/organizations.service';

export default function NoOrganizationPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showJoinForm, setShowJoinForm] = useState(false);
  const [organizationName, setOrganizationName] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Extract token from invite link
  function extractTokenFromLink(link: string): string | null {
    try {
      // Handle full URLs like https://example.com/invite/abc123
      if (link.includes('/invite/')) {
        const parts = link.split('/invite/');
        return parts[1]?.split(/[?#]/)[0] || null;
      }
      // Handle just the token
      if (link.length === 64 && /^[a-f0-9]+$/.test(link)) {
        return link;
      }
      return null;
    } catch {
      return null;
    }
  }

  async function handleCreateOrganization() {
    if (!organizationName.trim()) {
      setError('Please enter an organization name');
      return;
    }

    if (!user) return;

    try {
      setIsLoading(true);
      setError('');

      // Create the organization
      const orgId = await organizationsService.createOrganization(
        organizationName.trim(),
        user.uid
      );

      // Update user with organization and owner role
      await authService.updateUserOrganizationWithEmployee(user.uid, orgId, 'owner');

      // Sync member to organization
      await organizationsService.syncMember(orgId, user.uid, {
        email: user.email || '',
        displayName: user.displayName || '',
        role: 'owner',
      });

      enqueueSnackbar('Organization created successfully!', { variant: 'success' });
      navigate('/dashboard');
    } catch (err) {
      console.error('Create organization error:', err);
      const message = err instanceof Error ? err.message : 'Failed to create organization';
      setError(message);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }

  async function handleJoinOrganization() {
    const token = extractTokenFromLink(inviteLink.trim());

    if (!token) {
      setError('Please enter a valid invitation link or token');
      return;
    }

    // Navigate to the invite page with the extracted token
    navigate(`/invite/${token}`);
  }

  async function handleLogout() {
    try {
      await logout();
      navigate('/auth');
    } catch (err) {
      console.error('Logout error:', err);
      enqueueSnackbar('Failed to sign out', { variant: 'error' });
    }
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
        }}
      >
        <Paper sx={{ p: 4, width: '100%' }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <BusinessIcon sx={{ fontSize: 32, color: 'white' }} />
            </Box>
            <Typography variant="h5" gutterBottom>
              Welcome to ShiftGrid
            </Typography>
            <Typography color="text.secondary">
              You're not currently part of any organization.
            </Typography>
            {user?.email && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Signed in as {user.email}
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Main Options */}
          {!showCreateForm && !showJoinForm && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<LinkIcon />}
                onClick={() => setShowJoinForm(true)}
                fullWidth
              >
                Join an Organization
              </Button>
              <Typography variant="body2" color="text.secondary" align="center">
                Have an invitation link? Paste it to join an existing organization.
              </Typography>

              <Divider sx={{ my: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  or
                </Typography>
              </Divider>

              <Button
                variant="outlined"
                size="large"
                startIcon={<AddIcon />}
                onClick={() => setShowCreateForm(true)}
                fullWidth
              >
                Create New Organization
              </Button>
              <Typography variant="body2" color="text.secondary" align="center">
                Start fresh and set up your own organization.
              </Typography>
            </Box>
          )}

          {/* Join Form */}
          {showJoinForm && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Join an Organization
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Paste the invitation link you received from your organization.
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                label="Invitation Link"
                placeholder="https://... or paste the invite code"
                value={inviteLink}
                onChange={(e) => {
                  setInviteLink(e.target.value);
                  setError('');
                }}
                fullWidth
                margin="normal"
                disabled={isLoading}
                helperText="Paste the full invitation link or just the invite code"
              />

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setShowJoinForm(false);
                    setInviteLink('');
                    setError('');
                  }}
                  disabled={isLoading}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleJoinOrganization}
                  disabled={isLoading || !inviteLink.trim()}
                  fullWidth
                >
                  {isLoading ? <CircularProgress size={24} /> : 'Continue'}
                </Button>
              </Box>
            </Box>
          )}

          {/* Create Form */}
          {showCreateForm && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Create New Organization
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                You'll be the owner of this organization and can invite team members later.
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                label="Organization Name"
                placeholder="Your company or shop name"
                value={organizationName}
                onChange={(e) => {
                  setOrganizationName(e.target.value);
                  setError('');
                }}
                fullWidth
                margin="normal"
                disabled={isLoading}
              />

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setShowCreateForm(false);
                    setOrganizationName('');
                    setError('');
                  }}
                  disabled={isLoading}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleCreateOrganization}
                  disabled={isLoading || !organizationName.trim()}
                  fullWidth
                >
                  {isLoading ? <CircularProgress size={24} /> : 'Create Organization'}
                </Button>
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Sign Out */}
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="text"
              color="inherit"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              size="small"
            >
              Sign in with a different account
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}
